/* eslint-disable no-console */
// Browser-compatible test command that can be run from the console
import { getGameEngine } from './GameEngine';
import {
  getCurrentTick,
  waitForTicks,
  waitForNextTick,
  resetTickCount,
  enableTestingMode,
  disableTestingMode,
  advanceGameTick,
  isInTestingMode,
  enableDebugStepMode,
  disableDebugStepMode,
  stepAndPause,
  isInDebugStepMode,
} from './hooks/useGameTick';
import { TestRunner } from './TestRunner';
import { runAllGameTests } from './tests';
import { TestGlobals } from './types/TestGlobals';

// Global function to run tests from browser console
const runGameTests = async (): Promise<void> => {
  try {
    console.clear();
    await runAllGameTests();
  } catch (error) {
    console.error('❌ Test execution failed:', error);
  }
};

// Global function to get and log game state
const getGameState = () => {
  const gameEngine = getGameEngine();
  const gameState = gameEngine.getState();
  console.log('🎮 Current Game State:', JSON.stringify(gameState, null, 2));
  return gameState;
};

// Function to set up test globals (called explicitly from App.tsx)
export const setupTestGlobals = (): void => {
  if (typeof window !== 'undefined') {
    // Create typed test globals object
    const testGlobals: TestGlobals = {
      runGameTests,
      TestRunner,
      runAllGameTests,
      // Tick system methods for deterministic testing
      getCurrentTick,
      waitForTicks,
      waitForNextTick,
      resetTickCount,
      // Testing mode methods
      enableTestingMode,
      disableTestingMode,
      advanceGameTick,
      isInTestingMode,
      setUnlimitedPower: (enabled: boolean) => {
        const engine = getGameEngine();
        engine.setUnlimitedPowerMode(enabled);
      },
      isUnlimitedPowerEnabled: () => {
        const engine = getGameEngine();
        return engine.isUnlimitedPowerModeEnabled();
      },
      // Debug step mode methods
      enableDebugStepMode,
      disableDebugStepMode,
      stepAndPause,
      isInDebugStepMode,
      // Game state access
      getGameState,
    };

    // Expose as a single typed object
    window.testGlobals = testGlobals;

    // Also expose individual functions for backward compatibility
    // Using window object extension for legacy support
    const windowWithLegacy = window as typeof window & {
      runGameTests: typeof runGameTests;
      TestRunner: typeof TestRunner;
      runAllGameTests: typeof runAllGameTests;
      getCurrentTick: typeof getCurrentTick;
      waitForTicks: typeof waitForTicks;
      waitForNextTick: typeof waitForNextTick;
      resetTickCount: typeof resetTickCount;
      enableTestingMode: typeof enableTestingMode;
      disableTestingMode: typeof disableTestingMode;
      advanceGameTick: typeof advanceGameTick;
      isInTestingMode: typeof isInTestingMode;
      enableDebugStepMode: typeof enableDebugStepMode;
      disableDebugStepMode: typeof disableDebugStepMode;
      stepAndPause: typeof stepAndPause;
      isInDebugStepMode: typeof isInDebugStepMode;
      getGameState: typeof getGameState;
    };
    windowWithLegacy.runGameTests = runGameTests;
    windowWithLegacy.TestRunner = TestRunner;
    windowWithLegacy.runAllGameTests = runAllGameTests;
    windowWithLegacy.getCurrentTick = getCurrentTick;
    windowWithLegacy.waitForTicks = waitForTicks;
    windowWithLegacy.waitForNextTick = waitForNextTick;
    windowWithLegacy.resetTickCount = resetTickCount;
    windowWithLegacy.enableTestingMode = enableTestingMode;
    windowWithLegacy.disableTestingMode = disableTestingMode;
    windowWithLegacy.advanceGameTick = advanceGameTick;
    windowWithLegacy.isInTestingMode = isInTestingMode;
    windowWithLegacy.enableDebugStepMode = enableDebugStepMode;
    windowWithLegacy.disableDebugStepMode = disableDebugStepMode;
    windowWithLegacy.stepAndPause = stepAndPause;
    windowWithLegacy.isInDebugStepMode = isInDebugStepMode;
    windowWithLegacy.getGameState = getGameState;

    console.log('✅ Test globals initialized for Playwright');
  }
};

// Set up immediately when module loads
setupTestGlobals();

// Also export for import usage
export { runAllGameTests, TestRunner };

// Display help message in console
console.log(`
🎮 FactoryGPT Test Runner & Debug Tools
======================================

To run all tests, type in the browser console:
  runGameTests()

Available test commands:
  - runGameTests()    : Run all tests

Available debug commands:
  - stepAndPause()           : Enable single-step debugging and advance 1 tick
  - enableDebugStepMode()    : Enable debug step mode (pause automatic ticks)
  - disableDebugStepMode()   : Disable debug step mode (resume normal gameplay)
  - isInDebugStepMode()      : Check if debug step mode is active
  - getCurrentTick()         : Get current game tick number
  - getGameState()           : Log and return current game state
  
Tests can also be run by importing from './tests'
`);
